import { XMarkIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import type { FC } from 'react';

import { TemplateAPIs } from '@/api';
import { usePublishTemplate } from '@/api/queries/template';
import type { TemplateDetails } from '@/api/types/template';
import { Button, ButtonSize, ButtonType } from '@/common/button';
import { CopyIcon } from '@/common/copy-icon';
import { CustomToast } from '@/common/custom-toast';
import { InfoWarnIcon } from '@/common/info-warn-icon';
import { Modal } from '@/common/modal';
import { RoundedCheckIcon } from '@/common/rounded-check-icon';
import { TextField, TextFieldType } from '@/common/text-field';
import { timeAgo } from '@/utils';

interface PublishTemplateModalProps {
    onCancel?: () => void;
    onPublish?: () => void;
    template: TemplateDetails;
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
}

export const PublishTemplateModal: FC<PublishTemplateModalProps> = ({
    onCancel,
    onPublish,
    template,
    isOpen,
    setIsOpen,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [shareMail, setShareMail] = useState('');

    const { mutate: publishTemplate } = usePublishTemplate({
        onSuccess: () => {
            CustomToast.success('Template published successfully');
            setIsLoading(false);
            onPublish?.();
        },
        onError: () => {
            CustomToast.error('Failed to publish template');
            setIsLoading(false);
        },
    });

    const shareTemplate = () => async () => {
        if (shareMail) {
            setIsLoading(true);
            await TemplateAPIs.shareTemplate(template.id, shareMail);
            CustomToast.success('Mail sent successfully');
            setShareMail('');
            setIsLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen}>
            <div className="flex flex-col absolute min-w-96 w-[36vw] bg-transparent shadow-xl transition-all duration-300 ease-in-out">
                <div className="flex flex-col rounded-xl bg-gray-150">
                    <div className="flex flex-row justify-between p-6 items-center">
                        <p className="text-xl text-white">Publish Template</p>
                        <XMarkIcon
                            className="h-5 w-5 text-white hover:cursor-pointer hover:text-gray-30"
                            onClick={isLoading ? () => {} : () => setIsOpen(false)}
                        />
                    </div>
                    <hr className="bg-gray-100 border-gray-100" />

                    {!template.publishedVersion && (
                        <p className="text-sm text-white p-6">
                            When you publish this template, everyone in your team on Intaglio will have access to this
                            template.
                        </p>
                    )}
                    {template.publishedVersion && !template.latestVersion.isEdited && (
                        <div className="flex fled-row items-center p-6 gap-2">
                            <RoundedCheckIcon size={20} />
                            <div className="flex flex-row justify-between w-full">
                                <p className="text-sm text-green-60">This Template is published.</p>
                                <p className="text-sm text-white">
                                    {timeAgo(template.publishedVersion.publishedAt as string)}
                                </p>
                            </div>
                        </div>
                    )}
                    {template.publishedVersion && template.latestVersion.isEdited && (
                        <div className="flex fled-row p-6 gap-2">
                            <InfoWarnIcon />
                            <div className="flex flex-row justify-between w-full">
                                <p className="text-sm text-orange-50">Your published template has unsaved changes.</p>
                                <p className="text-xs text-white text-right">
                                    Last Published
                                    <br />
                                    {timeAgo(template.publishedVersion.publishedAt as string)}
                                </p>
                            </div>
                        </div>
                    )}
                    {template.publishedVersion && template.latestVersion.isEdited && (
                        <p className="text-sm text-white px-6">
                            This template contains modifications that haven't been applied yet.
                            <br />
                            To make these changes visible for your team, publish the template again.
                        </p>
                    )}

                    {(!template.publishedVersion || template.latestVersion.isEdited) && (
                        <div className="w-full flex flex-row justify-end gap-3 p-6">
                            <Button
                                title="Cancel"
                                className="px-5"
                                disabled={isLoading}
                                buttonType={ButtonType.SECONDARY}
                                buttonSize={ButtonSize.LARGE}
                                onClick={() => {
                                    setIsOpen(false);
                                    onCancel?.();
                                }}
                            />
                            <Button
                                title="Publish"
                                isLoading={isLoading}
                                disabled={isLoading}
                                buttonType={ButtonType.PRIMARY}
                                className="bg-blue-60 text-white px-8"
                                buttonSize={ButtonSize.LARGE}
                                onClick={() => {
                                    setIsLoading(true);
                                    publishTemplate(template.id);
                                }}
                            />
                        </div>
                    )}
                </div>
                {template.publishedVersion && (
                    <div className="flex flex-col rounded-xl bg-gray-150 mt-4 p-6">
                        <p className="text-sm text-white ml-1">Share Link</p>
                        <div className="flex flex-row bg-gray-100 items-center justify-between p-3 rounded-md mt-2">
                            <p className="text-white text-sm">{window.location.href}</p>
                            <button
                                type="button"
                                className="text-blue-60 text-sm"
                                onClick={() => {
                                    CustomToast.success('Link copied to clipboard');
                                    navigator.clipboard.writeText(window.location.href);
                                }}
                            >
                                <CopyIcon className="h-4 w-4 stroke-blue-30" />
                            </button>
                        </div>
                        <p className="text-sm text-white ml-1 mt-6">Send via Email</p>
                        <div className="flex flex-row items-center justify-between mt-2 gap-2">
                            <TextField
                                className="rounded-md w-full"
                                id="share-template-mail"
                                type={TextFieldType.Email}
                                value={shareMail}
                                onChange={setShareMail}
                                placeholder="person@gmail.com"
                            />
                            <Button
                                title="Send"
                                className="px-12 rounded-md"
                                buttonType={ButtonType.SECONDARY}
                                buttonSize={ButtonSize.LARGE}
                                disabled={isLoading || !shareMail}
                                onClick={shareTemplate()}
                            />
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
};
